import React from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, percent } from "../../../../utils/numbers";
import ActiveUsersTrend from "./ActiveUsersTrend";

/** Stacked bar with its legend, e.g. free vs subscribed users. A part's `hint` shows on hover. */
export const Split = ({ title, parts }) => {
  const total = parts.reduce((sum, part) => sum + part.value, 0);
  return (
    <div className="dash-split">
      <p className="dash-split-title">{title}</p>
      <div className="dash-split-bar" role="img" aria-label={title}>
        {parts.filter((part) => part.value > 0).map((part) => (
          <span
            key={part.label}
            style={{
              width: total > 0 ? `${(part.value / total) * 100}%` : 0,
              background: part.color,
            }}
          />
        ))}
      </div>
      <ul className="dash-legend">
        {parts.map((part) => (
          <li key={part.label} title={part.hint}>
            <span
              className="dash-legend-dot"
              style={{ background: part.color }}
            />
            <span>
              {part.label} <strong>{formatNumber(part.value)}</strong> (
              {percent(part.value, total)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Users total with their subscription and activity breakdowns. */
const UsersOverview = ({ counts }) => {
  const { t } = useTranslation();
  const toNumber = (value) => (Number.isFinite(value) ? value : 0);

  return (
    <section className="dash-card">
      <div className="dash-card-header">
        <h2 className="dash-card-title">{t("Users")}</h2>
      </div>
      {counts.loading ? (
        <div className="dash-skeleton" style={{ height: 180 }} />
      ) : (
        <>
          <div className="dash-users-total">
            <span className="dash-users-total-value">
              {formatNumber(toNumber(counts.total))}
            </span>
            <span className="dash-users-total-label">{t("Total users")}</span>
          </div>
          <ActiveUsersTrend />
          <Split
            title={t("Subscription")}
            parts={[
              {
                label: t("Free"),
                value: toNumber(counts.free),
                color: "var(--dash-slate)",
              },
              {
                label: t("Subscribed"),
                value: toNumber(counts.paid),
                color: "var(--dash-amber)",
              },
            ]}
          />
          {counts.activity && (
            <Split
              title={t("App activity")}
              parts={[
                {
                  label: t("Active"),
                  hint: t("seen in the last 30 days"),
                  value: counts.activity.active,
                  color: "var(--dash-blue)",
                },
                {
                  label: t("Inactive"),
                  hint: t("not seen for 30 days"),
                  value: counts.activity.inactive,
                  color: "var(--dash-slate)",
                },
                {
                  label: t("Never seen"),
                  hint: t("no recorded activity"),
                  value: counts.activity.neverSeen,
                  color: "var(--dash-never)",
                },
              ]}
            />
          )}
          <Split
            title={t("Account status")}
            parts={[
              {
                label: t("Enabled"),
                value: toNumber(counts.enabled),
                color: "var(--dash-green)",
              },
              {
                label: t("Disabled"),
                value: toNumber(counts.disabled),
                color: "var(--dash-red)",
              },
            ]}
          />
        </>
      )}
    </section>
  );
};

export default UsersOverview;
